import {
  C, bitmapPixelMask, createBitmap, drawBitmap, mirrorBitmap,
  type Bitmap, type GlowSource, type PixelMask, type SpectrumColor,
} from 'zx-kit'
import {
  GLOW_CORE_INTENSITY,
  TRUCK_GLOW_BRAKE_INTENSITY, TRUCK_GLOW_BRAKE_RADIUS,
  TRUCK_GLOW_CORE_RADIUS, TRUCK_GLOW_INTENSITY, TRUCK_GLOW_RADIUS,
} from '../../config.ts'
import { glowRadiusScale } from '../vehicleGlow.ts'

/** Code-native articulated road train, viewed from behind and slightly above. */
export const PLAYER_TRUCK_W = 40
export const PLAYER_TRUCK_H = 64

const CAB_W = 24
const CAB_H = 24
const CAB_Y = 0
const TRAILER_W = 40
const TRAILER_H = 60
const TRAILER_X = 0
const TRAILER_Y = 4

export type TruckAngle = -2 | -1 | 0 | 1 | 2
export const PLAYER_TRUCK_ANGLES: readonly TruckAngle[] = [-2, -1, 0, 1, 2]

const CAB_X_BY_ANGLE: Readonly<Record<TruckAngle, number>> = {
  [-2]: 0, [-1]: 4, [0]: 8, [1]: 12, [2]: 16,
}

export const PLAYER_TRUCK_LAYOUT = {
  cab: { y: CAB_Y, width: CAB_W, height: CAB_H, xByAngle: CAB_X_BY_ANGLE },
  trailer: { x: TRAILER_X, y: TRAILER_Y, width: TRAILER_W, height: TRAILER_H },
} as const

export interface PlayerTruckLayer {
  readonly x: number
  readonly y: number
  readonly bitmap: Bitmap
}

function layer(x: number, y: number, width: number, height: number, data: Uint8Array): PlayerTruckLayer {
  return { x, y, bitmap: createBitmap(data, width, height) }
}

// Generated workbench output; copy byte data into Ice Haul.
const CAB_M2_BASE = layer(0, 4, 24, 19, new Uint8Array([
  0x7F, 0xF8, 0x00, 0xFF, 0xFC, 0x00, 0xFF, 0xFF, 0x00, 0xFF, 0xFF, 0x00,
  0xFF, 0xFF, 0x80, 0xFF, 0xFF, 0xC0, 0xFF, 0xFF, 0xE0, 0xFF, 0xFF, 0xE0,
  0xFF, 0xFF, 0xE0, 0xFF, 0xFF, 0xE0, 0xFF, 0xFF, 0xF0, 0xFF, 0xFF, 0xF0,
  0xFF, 0xFF, 0xF0, 0xFF, 0xFF, 0xF0, 0x7F, 0xFF, 0xF8, 0x3F, 0xFF, 0xF0,
  0x1F, 0xFF, 0xE0, 0x1F, 0xFF, 0xE0, 0x07, 0xFF, 0xE0,
]))
const CAB_M2_RED = layer(0, 5, 24, 16, new Uint8Array([
  0x3F, 0xF0, 0x00, 0x3F, 0xFC, 0x00, 0x3F, 0xFC, 0x00, 0x7F, 0xFE, 0x00,
  0xFF, 0xFF, 0x00, 0x7F, 0xFF, 0x80, 0x7F, 0xFF, 0x80, 0x7F, 0xFF, 0x80,
  0x7F, 0xFF, 0x80, 0x3F, 0xFF, 0xC0, 0x3F, 0xFF, 0xC0, 0x3F, 0xFF, 0xC0,
  0x3F, 0xFF, 0xC0, 0x1F, 0xFF, 0xE0, 0x0F, 0xFF, 0xC0, 0x07, 0xFF, 0x80,
]))
const CAB_M2_DETAIL = layer(0, 6, 24, 13, new Uint8Array([
  0x03, 0x80, 0x00, 0x03, 0x80, 0x00, 0x03, 0x80, 0x00, 0x03, 0x80, 0x00,
  0x01, 0xC0, 0x00, 0x3F, 0xFF, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x07, 0xFF, 0x80,
]))
const CAB_M2_CYAN = layer(0, 7, 16, 4, new Uint8Array([
  0x1F, 0xF8, 0x3F, 0xFC, 0x3F, 0xFC, 0x3F, 0xFF,
]))
const CAB_M1_BASE = layer(0, 4, 24, 19, new Uint8Array([
  0x0F, 0xFF, 0x00, 0x1F, 0xFF, 0x80, 0x3F, 0xFF, 0xC0, 0x3F, 0xFF, 0xC0,
  0x7F, 0xFF, 0xE0, 0xFF, 0xFF, 0xF0, 0xFF, 0xFF, 0xF0, 0xFF, 0xFF, 0xF0,
  0x7F, 0xFF, 0xF8, 0x7F, 0xFF, 0xF8, 0x7F, 0xFF, 0xF8, 0x7F, 0xFF, 0xF8,
  0x7F, 0xFF, 0xF8, 0x7F, 0xFF, 0xF8, 0x7F, 0xFF, 0xF8, 0x3F, 0xFF, 0xF0,
  0x0F, 0xFF, 0xF0, 0x0F, 0xFF, 0xF0, 0x07, 0xFF, 0xE0,
]))
const CAB_M1_RED = layer(0, 5, 24, 16, new Uint8Array([
  0x07, 0xFE, 0x00, 0x0F, 0xFF, 0x00, 0x0F, 0xFF, 0x00, 0x1F, 0xFF, 0x80,
  0x3F, 0xFF, 0xC0, 0x3F, 0xFF, 0xC0, 0x3F, 0xFF, 0xC0, 0x1F, 0xFF, 0xE0,
  0x1F, 0xFF, 0xE0, 0x1F, 0xFF, 0xE0, 0x1F, 0xFF, 0xE0, 0x1F, 0xFF, 0xE0,
  0x1F, 0xFF, 0xE0, 0x1F, 0xFF, 0xE0, 0x0F, 0xFF, 0xC0, 0x03, 0xFF, 0xC0,
]))
const CAB_M1_DETAIL = layer(0, 6, 24, 13, new Uint8Array([
  0x00, 0xE0, 0x00, 0x00, 0xE0, 0x00, 0x00, 0xE0, 0x00, 0x00, 0xE0, 0x00,
  0x00, 0xE0, 0x00, 0x1F, 0xFF, 0x80, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x07, 0xFF, 0x80,
]))
const CAB_M1_CYAN = layer(0, 7, 24, 4, new Uint8Array([
  0x07, 0xFE, 0x00, 0x0F, 0xFF, 0x00, 0x0F, 0xFF, 0x00, 0x1F, 0xFF, 0x80,
]))
const CAB_0_BASE = layer(0, 4, 24, 19, new Uint8Array([
  0x03, 0xFF, 0xC0, 0x07, 0xFF, 0xE0, 0x0F, 0xFF, 0xF0, 0x0F, 0xFF, 0xF0,
  0x1F, 0xFF, 0xF8, 0x3F, 0xFF, 0xFC, 0x3F, 0xFF, 0xFC, 0x3F, 0xFF, 0xFC,
  0x3F, 0xFF, 0xFC, 0x3F, 0xFF, 0xFC, 0x3F, 0xFF, 0xFC, 0x3F, 0xFF, 0xFC,
  0x3F, 0xFF, 0xFC, 0x3F, 0xFF, 0xFC, 0x3F, 0xFF, 0xFC, 0x1F, 0xFF, 0xF8,
  0x0F, 0xFF, 0xF0, 0x0F, 0xFF, 0xF0, 0x07, 0xFF, 0xE0,
]))
const CAB_0_RED = layer(0, 5, 24, 16, new Uint8Array([
  0x01, 0xFF, 0x80, 0x03, 0xFF, 0xC0, 0x03, 0xFF, 0xC0, 0x07, 0xFF, 0xE0,
  0x0F, 0xFF, 0xF0, 0x0F, 0xFF, 0xF0, 0x0F, 0xFF, 0xF0, 0x0F, 0xFF, 0xF0,
  0x0F, 0xFF, 0xF0, 0x0F, 0xFF, 0xF0, 0x0F, 0xFF, 0xF0, 0x0F, 0xFF, 0xF0,
  0x0F, 0xFF, 0xF0, 0x0F, 0xFF, 0xF0, 0x07, 0xFF, 0xE0, 0x03, 0xFF, 0xC0,
]))
const CAB_0_DETAIL = layer(0, 6, 24, 13, new Uint8Array([
  0x00, 0x38, 0x00, 0x00, 0x38, 0x00, 0x00, 0x38, 0x00, 0x00, 0x38, 0x00,
  0x00, 0x38, 0x00, 0x07, 0xFF, 0xE0, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x03, 0xFF, 0xC0,
]))
const CAB_0_CYAN = layer(0, 7, 24, 4, new Uint8Array([
  0x01, 0xFF, 0x80, 0x03, 0xFF, 0xC0, 0x03, 0xFF, 0xC0, 0x07, 0xFF, 0xE0,
]))
const TRAILER_M2_BASE = layer(0, 0, 40, 60, new Uint8Array([
  0x01, 0xFF, 0xF8, 0x00, 0x00, 0x03, 0xFF, 0xFC, 0x00, 0x00, 0x07, 0xFF,
  0xFE, 0x00, 0x00, 0x0F, 0xFF, 0xFF, 0x00, 0x00, 0x1F, 0xFF, 0xFF, 0x80,
  0x00, 0x1F, 0xFF, 0xFF, 0xE0, 0x00, 0x3F, 0xFF, 0xFF, 0xF0, 0x00, 0x7F,
  0xFF, 0xFF, 0xF8, 0x00, 0xFF, 0xFF, 0xFF, 0xFC, 0x00, 0xFF, 0xFF, 0xFF,
  0xFC, 0x00, 0xFF, 0xFF, 0xFF, 0xFE, 0x00, 0xFF, 0xFF, 0xFF, 0xFE, 0x00,
  0xFF, 0xFF, 0xFF, 0xFF, 0x00, 0xFF, 0xFF, 0xFF, 0xFF, 0x00, 0xFF, 0xFF,
  0xFF, 0xFF, 0x80, 0xFF, 0xFF, 0xFF, 0xFF, 0xC0, 0xFF, 0xFF, 0xFF, 0xFF,
  0xC0, 0xFF, 0xFF, 0xFF, 0xFF, 0xC0, 0xFF, 0xFF, 0xFF, 0xFF, 0xC0, 0xFF,
  0xFF, 0xFF, 0xFF, 0xC0, 0xFF, 0xFF, 0xFF, 0xFF, 0xC0, 0xFF, 0xFF, 0xFF,
  0xFF, 0xC0, 0xFF, 0xFF, 0xFF, 0xFF, 0xC0, 0xFF, 0xFF, 0xFF, 0xFF, 0xC0,
  0xFF, 0xFF, 0xFF, 0xFF, 0xC0, 0xFF, 0xFF, 0xFF, 0xFF, 0xE0, 0xFF, 0xFF,
  0xFF, 0xFF, 0xE0, 0xFF, 0xFF, 0xFF, 0xFF, 0xE0, 0xFF, 0xFF, 0xFF, 0xFF,
  0xE0, 0xFF, 0xFF, 0xFF, 0xFF, 0xE0, 0xFF, 0xFF, 0xFF, 0xFF, 0xE0, 0xFF,
  0xFF, 0xFF, 0xFF, 0xF0, 0xFF, 0xFF, 0xFF, 0xFF, 0xF0, 0xFF, 0xFF, 0xFF,
  0xFF, 0xF0, 0xFF, 0xFF, 0xFF, 0xFF, 0xF0, 0xFF, 0xFF, 0xFF, 0xFF, 0xF8,
  0xFF, 0xFF, 0xFF, 0xFF, 0xF8, 0xFF, 0xFF, 0xFF, 0xFF, 0xF8, 0xFF, 0xFF,
  0xFF, 0xFF, 0xF8, 0xFF, 0xFF, 0xFF, 0xFF, 0xF8, 0xFF, 0xFF, 0xFF, 0xFF,
  0xF8, 0xFF, 0xFF, 0xFF, 0xFF, 0xF8, 0xFF, 0xFF, 0xFF, 0xFF, 0xF8, 0xFF,
  0xFF, 0xFF, 0xFF, 0xF8, 0xFF, 0xFF, 0xFF, 0xFF, 0xF8, 0xFF, 0xFF, 0xFF,
  0xFF, 0xFC, 0xFF, 0xFF, 0xFF, 0xFF, 0xFC, 0xFF, 0xFF, 0xFF, 0xFF, 0xFC,
  0x7F, 0xFF, 0xFF, 0xFF, 0xF8, 0x7F, 0xFF, 0xFF, 0xFF, 0xF8, 0x3F, 0xFF,
  0xFF, 0xFF, 0xF0, 0x3F, 0xFF, 0xFF, 0xFF, 0xF0, 0x0F, 0xFF, 0xFF, 0xFF,
  0xC0, 0x03, 0xFF, 0xFF, 0xFF, 0x00, 0x03, 0xFF, 0xFF, 0xFF, 0x00, 0x01,
  0xFF, 0x81, 0xFF, 0x80, 0x01, 0xFF, 0x00, 0xFF, 0x80, 0x01, 0xFF, 0x00,
  0xFF, 0x80, 0x01, 0xFE, 0x00, 0x7F, 0x80, 0x00, 0x02, 0x00, 0x40, 0x00,
]))
const TRAILER_M2_BLUE = layer(0, 13, 40, 35, new Uint8Array([
  0xFF, 0xFC, 0xFF, 0xFC, 0x00, 0xFF, 0xFC, 0xFF, 0xFC, 0x00, 0x7F, 0xFE,
  0x7F, 0xFE, 0x00, 0x7F, 0xFE, 0x7F, 0xFE, 0x00, 0x7F, 0xFE, 0x7F, 0xFE,
  0x00, 0x7F, 0xFE, 0x7F, 0xFE, 0x00, 0x7F, 0xFE, 0x7F, 0xFE, 0x00, 0x7F,
  0xFE, 0x7F, 0xFE, 0x00, 0x7F, 0xFE, 0x7F, 0xFE, 0x00, 0x7F, 0xFE, 0x7F,
  0xFE, 0x00, 0x7F, 0xFE, 0x7F, 0xFE, 0x00, 0x7F, 0xFE, 0x7F, 0xFE, 0x00,
  0x3F, 0xFF, 0x3F, 0xFF, 0x00, 0x3F, 0xFF, 0x3F, 0xFF, 0x00, 0x3F, 0xFF,
  0x3F, 0xFF, 0x00, 0x3F, 0xFF, 0x3F, 0xFF, 0x00, 0x7F, 0xFF, 0x3F, 0xFF,
  0x80, 0x7F, 0xFF, 0x3F, 0xFF, 0x80, 0x7F, 0xFF, 0x3F, 0xFF, 0x80, 0x7F,
  0xFF, 0x3F, 0xFF, 0x80, 0x7F, 0xFF, 0x3F, 0xFF, 0x80, 0x7F, 0xFF, 0x3F,
  0xFF, 0x80, 0x3F, 0xFF, 0x9F, 0xFF, 0xC0, 0x3F, 0xFF, 0x9F, 0xFF, 0xC0,
  0x3F, 0xFF, 0x9F, 0xFF, 0xC0, 0x3F, 0xFF, 0x9F, 0xFF, 0xC0, 0x3F, 0xFF,
  0x9F, 0xFF, 0xC0, 0x3F, 0xFF, 0x9F, 0xFF, 0xC0, 0x3F, 0xFF, 0x9F, 0xFF,
  0xC0, 0x3F, 0xFF, 0x9F, 0xFF, 0xC0, 0x3F, 0xFF, 0x9F, 0xFF, 0xC0, 0x3F,
  0xFF, 0x9F, 0xFF, 0xC0, 0x0F, 0xFF, 0xCF, 0xFF, 0xC0, 0x0F, 0xF0, 0x00,
  0x3F, 0xC0, 0x04, 0x00, 0x00, 0x00, 0x80,
]))
const TRAILER_M2_BRIGHTBLUE = layer(0, 2, 40, 45, new Uint8Array([
  0x01, 0xFF, 0xF8, 0x00, 0x00, 0x03, 0xFF, 0xFC, 0x00, 0x00, 0x07, 0xFF,
  0xFE, 0x00, 0x00, 0x07, 0xFF, 0xFF, 0x80, 0x00, 0x07, 0xFF, 0xFF, 0x80,
  0x00, 0x0F, 0xFF, 0xFF, 0xC0, 0x00, 0x1F, 0xFF, 0xFF, 0xE0, 0x00, 0x3F,
  0xFF, 0xFF, 0xF0, 0x00, 0x7F, 0xFF, 0xFF, 0xF8, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x80, 0x00, 0x00, 0x04, 0x00,
  0x80, 0x00, 0x00, 0x06, 0x00, 0xC0, 0x00, 0x00, 0x03, 0x80, 0xC0, 0x00,
  0x00, 0x03, 0x80, 0xC0, 0x00, 0x00, 0x03, 0x80, 0xC0, 0x00, 0x00, 0x03,
  0x80, 0xC0, 0x00, 0x00, 0x03, 0x80, 0xC0, 0x00, 0x00, 0x03, 0x80, 0xC0,
  0x00, 0x00, 0x03, 0x80, 0xC0, 0x00, 0x00, 0x03, 0x80, 0xC0, 0x00, 0x00,
  0x03, 0x80, 0xC0, 0x00, 0x00, 0x03, 0x80, 0xE0, 0x00, 0x00, 0x01, 0xC0,
  0xE0, 0x00, 0x00, 0x01, 0xC0, 0xE0, 0x00, 0x00, 0x01, 0xC0, 0xE0, 0x00,
  0x00, 0x01, 0xC0, 0xE0, 0x00, 0x00, 0x01, 0xC0, 0xE0, 0x00, 0x00, 0x01,
  0xC0, 0xE0, 0x00, 0x00, 0x01, 0xC0, 0xE0, 0x00, 0x00, 0x01, 0xC0, 0xE0,
  0x00, 0x00, 0x01, 0xC0, 0xE0, 0x00, 0x00, 0x01, 0xC0, 0x70, 0x00, 0x00,
  0x00, 0xE0, 0x70, 0x00, 0x00, 0x00, 0xE0, 0x70, 0x00, 0x00, 0x00, 0xE0,
  0x70, 0x00, 0x00, 0x00, 0xE0, 0x70, 0x00, 0x00, 0x00, 0xE0, 0x70, 0x00,
  0x00, 0x00, 0xE0, 0x70, 0x00, 0x00, 0x00, 0xE0, 0x70, 0x00, 0x00, 0x00,
  0xE0, 0x70, 0x00, 0x00, 0x00, 0xE0, 0x70, 0x00, 0x00, 0x00, 0xE0, 0x18,
  0x00, 0x00, 0x00, 0x60, 0x08, 0x00, 0x00, 0x00, 0x40,
]))
const TRAILER_M2_DETAIL = layer(0, 12, 40, 39, new Uint8Array([
  0xFF, 0xFF, 0xFF, 0xFE, 0x00, 0xFF, 0xFF, 0xFF, 0xFE, 0x00, 0x00, 0x03,
  0x00, 0x00, 0x00, 0x00, 0x01, 0x80, 0x00, 0x00, 0x00, 0x01, 0x80, 0x00,
  0x00, 0x00, 0x01, 0x80, 0x00, 0x00, 0x38, 0x01, 0x80, 0x1C, 0x00, 0x38,
  0x01, 0x80, 0x1C, 0x00, 0x00, 0x01, 0x80, 0x00, 0x00, 0x00, 0x01, 0x80,
  0x00, 0x00, 0x00, 0x01, 0x80, 0x00, 0x00, 0x00, 0x01, 0x80, 0x00, 0x00,
  0xFF, 0xFF, 0xFF, 0xFF, 0x00, 0x00, 0x00, 0xC0, 0x00, 0x00, 0x00, 0x00,
  0xC0, 0x00, 0x00, 0x00, 0x00, 0xC0, 0x00, 0x00, 0x00, 0x00, 0xC0, 0x00,
  0x00, 0x00, 0x00, 0xC0, 0x00, 0x00, 0x00, 0x00, 0xC0, 0x00, 0x00, 0x1C,
  0x00, 0xC0, 0x0E, 0x00, 0x1C, 0x00, 0xC0, 0x0E, 0x00, 0x00, 0x00, 0xC0,
  0x00, 0x00, 0x00, 0x00, 0xC0, 0x00, 0x00, 0x3F, 0xFF, 0xFF, 0xFF, 0xC0,
  0x00, 0x01, 0xF8, 0x00, 0x00, 0x00, 0x01, 0xF8, 0x00, 0x00, 0x00, 0x01,
  0xF8, 0x00, 0x00, 0x00, 0x01, 0xF8, 0x00, 0x00, 0x00, 0x01, 0xF8, 0x00,
  0x00, 0x0E, 0x01, 0xF8, 0x07, 0x00, 0x0E, 0x00, 0x60, 0x07, 0x00, 0x00,
  0x00, 0x60, 0x00, 0x00, 0x00, 0x00, 0x60, 0x00, 0x00, 0x00, 0x00, 0x30,
  0x00, 0x00, 0x1F, 0xFF, 0xFF, 0xFF, 0xE0, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x0F, 0xFF, 0xFF, 0xFF, 0xC0, 0x0F, 0xFF,
  0xFF, 0xFF, 0xC0,
]))
const TRAILER_M2_CYAN = layer(0, 0, 40, 58, new Uint8Array([
  0x01, 0xFF, 0xF8, 0x00, 0x00, 0x02, 0x00, 0x04, 0x00, 0x00, 0x04, 0x00,
  0x02, 0x00, 0x00, 0x08, 0x00, 0x01, 0x00, 0x00, 0x08, 0x00, 0x01, 0x00,
  0x00, 0x08, 0x00, 0x00, 0x40, 0x00, 0x10, 0x00, 0x00, 0x20, 0x00, 0x20,
  0x00, 0x00, 0x10, 0x00, 0x20, 0x00, 0x00, 0x10, 0x00, 0x40, 0x00, 0x00,
  0x08, 0x00, 0x80, 0x00, 0x00, 0x04, 0x00, 0xFF, 0xFF, 0xFF, 0xFC, 0x00,
  0x00, 0x00, 0x00, 0x02, 0x00, 0x00, 0x00, 0x00, 0x02, 0x00, 0x00, 0x00,
  0x00, 0x02, 0x00, 0x00, 0x00, 0x00, 0x00, 0x80, 0x00, 0x00, 0x00, 0x00,
  0x80, 0x00, 0x00, 0x00, 0x00, 0x80, 0x00, 0x00, 0x00, 0x00, 0x80, 0x00,
  0x00, 0x00, 0x00, 0x80, 0x00, 0x00, 0x00, 0x00, 0x80, 0x00, 0x00, 0x00,
  0x00, 0x80, 0x00, 0x00, 0x00, 0x00, 0x80, 0x00, 0x00, 0x00, 0x00, 0x80,
  0x00, 0x00, 0x00, 0x00, 0x80, 0x80, 0x00, 0x00, 0x00, 0x40, 0x80, 0x00,
  0x00, 0x00, 0x40, 0x80, 0x00, 0x00, 0x00, 0x40, 0x80, 0x00, 0x00, 0x00,
  0x40, 0x80, 0x00, 0x00, 0x00, 0x40, 0x80, 0x00, 0x00, 0x00, 0x40, 0x80,
  0x00, 0x00, 0x00, 0x40, 0x80, 0x00, 0x00, 0x00, 0x40, 0x80, 0x00, 0x00,
  0x00, 0x40, 0x80, 0x00, 0x00, 0x00, 0x40, 0x40, 0x00, 0x00, 0x00, 0x20,
  0x40, 0x00, 0x00, 0x00, 0x20, 0x40, 0x00, 0x00, 0x00, 0x20, 0x40, 0x00,
  0x00, 0x00, 0x20, 0x40, 0x00, 0x00, 0x00, 0x20, 0x40, 0x00, 0x00, 0x00,
  0x20, 0x40, 0x00, 0x00, 0x00, 0x20, 0x40, 0x00, 0x00, 0x00, 0x20, 0x40,
  0x00, 0x00, 0x00, 0x20, 0x40, 0x00, 0x00, 0x00, 0x20, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x78, 0x00, 0x1E, 0x00, 0x00, 0x78, 0x00, 0x1E, 0x00, 0x00, 0x78, 0x00,
  0x1E, 0x00,
]))
const TRAILER_M2_WHITE = layer(0, 1, 32, 29, new Uint8Array([
  0x00, 0xFF, 0xF0, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x1F, 0xF8, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x60, 0x00, 0x00, 0x00, 0x30, 0x00, 0x00, 0x00,
  0x30, 0x00, 0x00, 0x00, 0x30, 0x00, 0x00, 0x00, 0x30, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x0C, 0x00, 0x00, 0x00, 0x0C, 0x00, 0x00, 0x00, 0x0C,
  0x00, 0x00, 0x00, 0x0C, 0x00, 0x00, 0x00, 0x0C,
]))
const TRAILER_M2_YELLOW = layer(16, 47, 8, 3, new Uint8Array([
  0xFC, 0xFC, 0xFC,
]))
const TRAILER_M2_LAMPS = layer(0, 43, 40, 4, new Uint8Array([
  0x1E, 0x00, 0x00, 0x07, 0x80, 0x1E, 0x00, 0x00, 0x07, 0x80, 0x0F, 0x00,
  0x00, 0x03, 0xC0, 0x0F, 0x00, 0x00, 0x03, 0xC0,
]))
const TRAILER_M1_BASE = layer(0, 0, 40, 60, new Uint8Array([
  0x00, 0x3F, 0xFF, 0x00, 0x00, 0x00, 0x7F, 0xFF, 0x80, 0x00, 0x00, 0xFF,
  0xFF, 0xC0, 0x00, 0x01, 0xFF, 0xFF, 0xE0, 0x00, 0x03, 0xFF, 0xFF, 0xF0,
  0x00, 0x07, 0xFF, 0xFF, 0xF8, 0x00, 0x0F, 0xFF, 0xFF, 0xFC, 0x00, 0x1F,
  0xFF, 0xFF, 0xFE, 0x00, 0x3F, 0xFF, 0xFF, 0xFF, 0x00, 0x3F, 0xFF, 0xFF,
  0xFF, 0x00, 0x3F, 0xFF, 0xFF, 0xFF, 0xC0, 0x3F, 0xFF, 0xFF, 0xFF, 0xC0,
  0x7F, 0xFF, 0xFF, 0xFF, 0xE0, 0x7F, 0xFF, 0xFF, 0xFF, 0xE0, 0xFF, 0xFF,
  0xFF, 0xFF, 0xF0, 0xFF, 0xFF, 0xFF, 0xFF, 0xF0, 0xFF, 0xFF, 0xFF, 0xFF,
  0xF0, 0xFF, 0xFF, 0xFF, 0xFF, 0xF0, 0xFF, 0xFF, 0xFF, 0xFF, 0xF0, 0xFF,
  0xFF, 0xFF, 0xFF, 0xF0, 0xFF, 0xFF, 0xFF, 0xFF, 0xF0, 0xFF, 0xFF, 0xFF,
  0xFF, 0xF0, 0xFF, 0xFF, 0xFF, 0xFF, 0xF0, 0xFF, 0xFF, 0xFF, 0xFF, 0xF0,
  0xFF, 0xFF, 0xFF, 0xFF, 0xF0, 0xFF, 0xFF, 0xFF, 0xFF, 0xF0, 0xFF, 0xFF,
  0xFF, 0xFF, 0xF0, 0xFF, 0xFF, 0xFF, 0xFF, 0xF0, 0xFF, 0xFF, 0xFF, 0xFF,
  0xF0, 0xFF, 0xFF, 0xFF, 0xFF, 0xF0, 0x7F, 0xFF, 0xFF, 0xFF, 0xF8, 0xFF,
  0xFF, 0xFF, 0xFF, 0xFC, 0xFF, 0xFF, 0xFF, 0xFF, 0xFC, 0xFF, 0xFF, 0xFF,
  0xFF, 0xFC, 0xFF, 0xFF, 0xFF, 0xFF, 0xFC, 0xFF, 0xFF, 0xFF, 0xFF, 0xFC,
  0xFF, 0xFF, 0xFF, 0xFF, 0xFC, 0xFF, 0xFF, 0xFF, 0xFF, 0xFC, 0xFF, 0xFF,
  0xFF, 0xFF, 0xFC, 0xFF, 0xFF, 0xFF, 0xFF, 0xFC, 0xFF, 0xFF, 0xFF, 0xFF,
  0xFC, 0xFF, 0xFF, 0xFF, 0xFF, 0xFC, 0xFF, 0xFF, 0xFF, 0xFF, 0xFC, 0xFF,
  0xFF, 0xFF, 0xFF, 0xFC, 0xFF, 0xFF, 0xFF, 0xFF, 0xFC, 0xFF, 0xFF, 0xFF,
  0xFF, 0xFC, 0xFF, 0xFF, 0xFF, 0xFF, 0xFC, 0xFF, 0xFF, 0xFF, 0xFF, 0xFC,
  0x7F, 0xFF, 0xFF, 0xFF, 0xF8, 0x7F, 0xFF, 0xFF, 0xFF, 0xF8, 0x1F, 0xFF,
  0xFF, 0xFF, 0xF8, 0x1F, 0xFF, 0xFF, 0xFF, 0xF8, 0x07, 0xFF, 0xFF, 0xFF,
  0xE0, 0x01, 0xFF, 0xFF, 0xFF, 0x80, 0x01, 0xFF, 0xFF, 0xFF, 0x80, 0x01,
  0xFF, 0x81, 0xFF, 0x80, 0x01, 0xFF, 0x00, 0xFF, 0x80, 0x01, 0xFF, 0x00,
  0xFF, 0x80, 0x01, 0xFE, 0x00, 0x7F, 0x80, 0x00, 0x02, 0x00, 0x40, 0x00,
]))
const TRAILER_M1_BLUE = layer(0, 13, 40, 35, new Uint8Array([
  0x1F, 0xFF, 0x9F, 0xFF, 0x80, 0x1F, 0xFF, 0x9F, 0xFF, 0x80, 0x1F, 0xFF,
  0x9F, 0xFF, 0x80, 0x1F, 0xFF, 0x9F, 0xFF, 0x80, 0x1F, 0xFF, 0x9F, 0xFF,
  0x80, 0x1F, 0xFF, 0x9F, 0xFF, 0x80, 0x1F, 0xFF, 0x9F, 0xFF, 0x80, 0x1F,
  0xFF, 0x9F, 0xFF, 0x80, 0x1F, 0xFF, 0x9F, 0xFF, 0x80, 0x1F, 0xFF, 0x9F,
  0xFF, 0x80, 0x1F, 0xFF, 0x9F, 0xFF, 0x80, 0x1F, 0xFF, 0x9F, 0xFF, 0x80,
  0x1F, 0xFF, 0x9F, 0xFF, 0x80, 0x1F, 0xFF, 0x9F, 0xFF, 0x80, 0x1F, 0xFF,
  0x9F, 0xFF, 0x80, 0x1F, 0xFF, 0x9F, 0xFF, 0x80, 0x3F, 0xFF, 0x9F, 0xFF,
  0xC0, 0x1F, 0xFF, 0xCF, 0xFF, 0xE0, 0x1F, 0xFF, 0xCF, 0xFF, 0xE0, 0x1F,
  0xFF, 0xCF, 0xFF, 0xE0, 0x1F, 0xFF, 0xCF, 0xFF, 0xE0, 0x1F, 0xFF, 0xCF,
  0xFF, 0xE0, 0x1F, 0xFF, 0xCF, 0xFF, 0xE0, 0x1F, 0xFF, 0xCF, 0xFF, 0xE0,
  0x1F, 0xFF, 0xCF, 0xFF, 0xE0, 0x1F, 0xFF, 0xCF, 0xFF, 0xE0, 0x1F, 0xFF,
  0xCF, 0xFF, 0xE0, 0x1F, 0xFF, 0xCF, 0xFF, 0xE0, 0x1F, 0xFF, 0xCF, 0xFF,
  0xE0, 0x1F, 0xFF, 0xCF, 0xFF, 0xE0, 0x1F, 0xFF, 0xCF, 0xFF, 0xE0, 0x1F,
  0xFF, 0xCF, 0xFF, 0xE0, 0x0F, 0xFF, 0xCF, 0xFF, 0xC0, 0x0F, 0xF0, 0x00,
  0x3F, 0xC0, 0x04, 0x00, 0x00, 0x00, 0x80,
]))
const TRAILER_M1_BRIGHTBLUE = layer(0, 2, 40, 45, new Uint8Array([
  0x00, 0x3F, 0xFF, 0x00, 0x00, 0x00, 0x7F, 0xFF, 0x80, 0x00, 0x00, 0xFF,
  0xFF, 0xC0, 0x00, 0x01, 0xFF, 0xFF, 0xE0, 0x00, 0x01, 0xFF, 0xFF, 0xE0,
  0x00, 0x03, 0xFF, 0xFF, 0xF0, 0x00, 0x07, 0xFF, 0xFF, 0xF8, 0x00, 0x0F,
  0xFF, 0xFF, 0xFC, 0x00, 0x0F, 0xFF, 0xFF, 0xFF, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x10, 0x00, 0x00, 0x00, 0x80,
  0x30, 0x00, 0x00, 0x00, 0xC0, 0x70, 0x00, 0x00, 0x00, 0xE0, 0x70, 0x00,
  0x00, 0x00, 0xE0, 0x70, 0x00, 0x00, 0x00, 0xE0, 0x70, 0x00, 0x00, 0x00,
  0xE0, 0x70, 0x00, 0x00, 0x00, 0xE0, 0x70, 0x00, 0x00, 0x00, 0xE0, 0x70,
  0x00, 0x00, 0x00, 0xE0, 0x70, 0x00, 0x00, 0x00, 0xE0, 0x70, 0x00, 0x00,
  0x00, 0xE0, 0x70, 0x00, 0x00, 0x00, 0xE0, 0x70, 0x00, 0x00, 0x00, 0xE0,
  0x70, 0x00, 0x00, 0x00, 0xE0, 0x70, 0x00, 0x00, 0x00, 0xE0, 0x70, 0x00,
  0x00, 0x00, 0xE0, 0x70, 0x00, 0x00, 0x00, 0xE0, 0x38, 0x00, 0x00, 0x00,
  0x70, 0x38, 0x00, 0x00, 0x00, 0x70, 0x38, 0x00, 0x00, 0x00, 0x70, 0x38,
  0x00, 0x00, 0x00, 0x70, 0x38, 0x00, 0x00, 0x00, 0x70, 0x38, 0x00, 0x00,
  0x00, 0x70, 0x38, 0x00, 0x00, 0x00, 0x70, 0x38, 0x00, 0x00, 0x00, 0x70,
  0x38, 0x00, 0x00, 0x00, 0x70, 0x38, 0x00, 0x00, 0x00, 0x70, 0x38, 0x00,
  0x00, 0x00, 0x70, 0x38, 0x00, 0x00, 0x00, 0x70, 0x38, 0x00, 0x00, 0x00,
  0x70, 0x38, 0x00, 0x00, 0x00, 0x70, 0x38, 0x00, 0x00, 0x00, 0x70, 0x18,
  0x00, 0x00, 0x00, 0x60, 0x08, 0x00, 0x00, 0x00, 0x40,
]))
const TRAILER_M1_DETAIL = layer(0, 12, 40, 39, new Uint8Array([
  0x3F, 0xFF, 0xFF, 0xFF, 0xC0, 0x3F, 0xFF, 0xFF, 0xFF, 0xC0, 0x00, 0x00,
  0x60, 0x00, 0x00, 0x00, 0x00, 0x60, 0x00, 0x00, 0x00, 0x00, 0x60, 0x00,
  0x00, 0x00, 0x00, 0x60, 0x00, 0x00, 0x0E, 0x00, 0x60, 0x07, 0x00, 0x0E,
  0x00, 0x60, 0x07, 0x00, 0x00, 0x00, 0x60, 0x00, 0x00, 0x00, 0x00, 0x60,
  0x00, 0x00, 0x00, 0x00, 0x60, 0x00, 0x00, 0x00, 0x00, 0x60, 0x00, 0x00,
  0x3F, 0xFF, 0xFF, 0xFF, 0xC0, 0x00, 0x00, 0x60, 0x00, 0x00, 0x00, 0x00,
  0x60, 0x00, 0x00, 0x00, 0x00, 0x60, 0x00, 0x00, 0x00, 0x00, 0x60, 0x00,
  0x00, 0x00, 0x00, 0x60, 0x00, 0x00, 0x00, 0x00, 0x30, 0x00, 0x00, 0x07,
  0x00, 0x30, 0x03, 0x80, 0x07, 0x00, 0x30, 0x03, 0x80, 0x00, 0x00, 0x30,
  0x00, 0x00, 0x00, 0x00, 0x30, 0x00, 0x00, 0x1F, 0xFF, 0xFF, 0xFF, 0xE0,
  0x00, 0x00, 0xFC, 0x00, 0x00, 0x00, 0x00, 0xFC, 0x00, 0x00, 0x00, 0x00,
  0xFC, 0x00, 0x00, 0x00, 0x00, 0xFC, 0x00, 0x00, 0x00, 0x00, 0xFC, 0x00,
  0x00, 0x07, 0x00, 0xFC, 0x03, 0x80, 0x07, 0x00, 0x30, 0x03, 0x80, 0x00,
  0x00, 0x30, 0x00, 0x00, 0x00, 0x00, 0x30, 0x00, 0x00, 0x00, 0x00, 0x30,
  0x00, 0x00, 0x1F, 0xFF, 0xFF, 0xFF, 0xE0, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x0F, 0xFF, 0xFF, 0xFF, 0xC0, 0x07, 0xFF,
  0xFF, 0xFF, 0xE0,
]))
const TRAILER_M1_CYAN = layer(0, 0, 40, 58, new Uint8Array([
  0x00, 0x3F, 0xFF, 0x00, 0x00, 0x00, 0x40, 0x00, 0x80, 0x00, 0x00, 0x80,
  0x00, 0x40, 0x00, 0x01, 0x00, 0x00, 0x20, 0x00, 0x01, 0x00, 0x00, 0x20,
  0x00, 0x02, 0x00, 0x00, 0x10, 0x00, 0x04, 0x00, 0x00, 0x08, 0x00, 0x08,
  0x00, 0x00, 0x04, 0x00, 0x08, 0x00, 0x00, 0x04, 0x00, 0x10, 0x00, 0x00,
  0x02, 0x00, 0x10, 0x00, 0x00, 0x00, 0x80, 0x1F, 0xFF, 0xFF, 0xFF, 0x80,
  0x20, 0x00, 0x00, 0x00, 0x40, 0x20, 0x00, 0x00, 0x00, 0x40, 0x20, 0x00,
  0x00, 0x00, 0x40, 0x40, 0x00, 0x00, 0x00, 0x20, 0x40, 0x00, 0x00, 0x00,
  0x20, 0x40, 0x00, 0x00, 0x00, 0x20, 0x40, 0x00, 0x00, 0x00, 0x20, 0x40,
  0x00, 0x00, 0x00, 0x20, 0x40, 0x00, 0x00, 0x00, 0x20, 0x40, 0x00, 0x00,
  0x00, 0x20, 0x40, 0x00, 0x00, 0x00, 0x20, 0x40, 0x00, 0x00, 0x00, 0x20,
  0x40, 0x00, 0x00, 0x00, 0x20, 0x40, 0x00, 0x00, 0x00, 0x20, 0x40, 0x00,
  0x00, 0x00, 0x20, 0x40, 0x00, 0x00, 0x00, 0x20, 0x40, 0x00, 0x00, 0x00,
  0x20, 0x40, 0x00, 0x00, 0x00, 0x20, 0x20, 0x00, 0x00, 0x00, 0x10, 0x20,
  0x00, 0x00, 0x00, 0x10, 0x20, 0x00, 0x00, 0x00, 0x10, 0x20, 0x00, 0x00,
  0x00, 0x10, 0x20, 0x00, 0x00, 0x00, 0x10, 0x20, 0x00, 0x00, 0x00, 0x10,
  0x20, 0x00, 0x00, 0x00, 0x10, 0x20, 0x00, 0x00, 0x00, 0x10, 0x20, 0x00,
  0x00, 0x00, 0x10, 0x20, 0x00, 0x00, 0x00, 0x10, 0x20, 0x00, 0x00, 0x00,
  0x10, 0x20, 0x00, 0x00, 0x00, 0x10, 0x20, 0x00, 0x00, 0x00, 0x10, 0x20,
  0x00, 0x00, 0x00, 0x10, 0x20, 0x00, 0x00, 0x00, 0x10, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x78, 0x00, 0x1E, 0x00, 0x00, 0x78, 0x00, 0x1E, 0x00, 0x00, 0x78, 0x00,
  0x1E, 0x00,
]))
const TRAILER_M1_WHITE = layer(0, 1, 32, 29, new Uint8Array([
  0x00, 0x1F, 0xFE, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x07, 0xFE, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x0C, 0x00, 0x00, 0x00, 0x0C, 0x00, 0x00, 0x00,
  0x0C, 0x00, 0x00, 0x00, 0x0C, 0x00, 0x00, 0x00, 0x0C, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x06, 0x00, 0x00, 0x00, 0x06, 0x00, 0x00, 0x00, 0x06,
  0x00, 0x00, 0x00, 0x06, 0x00, 0x00, 0x00, 0x06,
]))
const TRAILER_M1_YELLOW = layer(16, 47, 8, 3, new Uint8Array([
  0xFC, 0xFC, 0xFC,
]))
const TRAILER_M1_LAMPS = layer(0, 43, 40, 4, new Uint8Array([
  0x0F, 0x00, 0x00, 0x03, 0xC0, 0x0F, 0x00, 0x00, 0x03, 0xC0, 0x0F, 0x00,
  0x00, 0x03, 0xC0, 0x0F, 0x00, 0x00, 0x03, 0xC0,
]))
const TRAILER_0_BASE = layer(0, 0, 40, 60, new Uint8Array([
  0x00, 0x07, 0xFF, 0xE0, 0x00, 0x00, 0x0F, 0xFF, 0xF0, 0x00, 0x00, 0x1F,
  0xFF, 0xF8, 0x00, 0x00, 0x3F, 0xFF, 0xFC, 0x00, 0x00, 0x7F, 0xFF, 0xFE,
  0x00, 0x00, 0xFF, 0xFF, 0xFF, 0x00, 0x01, 0xFF, 0xFF, 0xFF, 0x80, 0x03,
  0xFF, 0xFF, 0xFF, 0xC0, 0x07, 0xFF, 0xFF, 0xFF, 0xE0, 0x07, 0xFF, 0xFF,
  0xFF, 0xE0, 0x0F, 0xFF, 0xFF, 0xFF, 0xF0, 0x0F, 0xFF, 0xFF, 0xFF, 0xF0,
  0x1F, 0xFF, 0xFF, 0xFF, 0xF8, 0x1F, 0xFF, 0xFF, 0xFF, 0xF8, 0x3F, 0xFF,
  0xFF, 0xFF, 0xFC, 0x3F, 0xFF, 0xFF, 0xFF, 0xFC, 0x3F, 0xFF, 0xFF, 0xFF,
  0xFC, 0x3F, 0xFF, 0xFF, 0xFF, 0xFC, 0x3F, 0xFF, 0xFF, 0xFF, 0xFC, 0x3F,
  0xFF, 0xFF, 0xFF, 0xFC, 0x3F, 0xFF, 0xFF, 0xFF, 0xFC, 0x3F, 0xFF, 0xFF,
  0xFF, 0xFC, 0x3F, 0xFF, 0xFF, 0xFF, 0xFC, 0x3F, 0xFF, 0xFF, 0xFF, 0xFC,
  0x3F, 0xFF, 0xFF, 0xFF, 0xFC, 0x3F, 0xFF, 0xFF, 0xFF, 0xFC, 0x3F, 0xFF,
  0xFF, 0xFF, 0xFC, 0x3F, 0xFF, 0xFF, 0xFF, 0xFC, 0x3F, 0xFF, 0xFF, 0xFF,
  0xFC, 0x3F, 0xFF, 0xFF, 0xFF, 0xFC, 0x3F, 0xFF, 0xFF, 0xFF, 0xFC, 0x7F,
  0xFF, 0xFF, 0xFF, 0xFE, 0x7F, 0xFF, 0xFF, 0xFF, 0xFE, 0x7F, 0xFF, 0xFF,
  0xFF, 0xFE, 0x7F, 0xFF, 0xFF, 0xFF, 0xFE, 0x7F, 0xFF, 0xFF, 0xFF, 0xFE,
  0x7F, 0xFF, 0xFF, 0xFF, 0xFE, 0x7F, 0xFF, 0xFF, 0xFF, 0xFE, 0x7F, 0xFF,
  0xFF, 0xFF, 0xFE, 0x7F, 0xFF, 0xFF, 0xFF, 0xFE, 0x7F, 0xFF, 0xFF, 0xFF,
  0xFE, 0x7F, 0xFF, 0xFF, 0xFF, 0xFE, 0x7F, 0xFF, 0xFF, 0xFF, 0xFE, 0x7F,
  0xFF, 0xFF, 0xFF, 0xFE, 0x7F, 0xFF, 0xFF, 0xFF, 0xFE, 0x7F, 0xFF, 0xFF,
  0xFF, 0xFE, 0x7F, 0xFF, 0xFF, 0xFF, 0xFE, 0x7F, 0xFF, 0xFF, 0xFF, 0xFE,
  0x3F, 0xFF, 0xFF, 0xFF, 0xFC, 0x3F, 0xFF, 0xFF, 0xFF, 0xFC, 0x1F, 0xFF,
  0xFF, 0xFF, 0xF8, 0x1F, 0xFF, 0xFF, 0xFF, 0xF8, 0x07, 0xFF, 0xFF, 0xFF,
  0xE0, 0x01, 0xFF, 0xFF, 0xFF, 0x80, 0x01, 0xFF, 0xFF, 0xFF, 0x80, 0x01,
  0xFF, 0x81, 0xFF, 0x80, 0x01, 0xFF, 0x00, 0xFF, 0x80, 0x01, 0xFF, 0x00,
  0xFF, 0x80, 0x01, 0xFE, 0x00, 0x7F, 0x80, 0x00, 0x02, 0x00, 0x40, 0x00,
]))
const TRAILER_0_BLUE = layer(0, 13, 40, 35, new Uint8Array([
  0x07, 0xFF, 0xE7, 0xFF, 0xE0, 0x07, 0xFF, 0xE7, 0xFF, 0xE0, 0x07, 0xFF,
  0xE7, 0xFF, 0xE0, 0x07, 0xFF, 0xE7, 0xFF, 0xE0, 0x07, 0xFF, 0xE7, 0xFF,
  0xE0, 0x07, 0xFF, 0xE7, 0xFF, 0xE0, 0x07, 0xFF, 0xE7, 0xFF, 0xE0, 0x07,
  0xFF, 0xE7, 0xFF, 0xE0, 0x07, 0xFF, 0xE7, 0xFF, 0xE0, 0x07, 0xFF, 0xE7,
  0xFF, 0xE0, 0x07, 0xFF, 0xE7, 0xFF, 0xE0, 0x07, 0xFF, 0xE7, 0xFF, 0xE0,
  0x07, 0xFF, 0xE7, 0xFF, 0xE0, 0x07, 0xFF, 0xE7, 0xFF, 0xE0, 0x07, 0xFF,
  0xE7, 0xFF, 0xE0, 0x07, 0xFF, 0xE7, 0xFF, 0xE0, 0x0F, 0xFF, 0xE7, 0xFF,
  0xF0, 0x0F, 0xFF, 0xE7, 0xFF, 0xF0, 0x0F, 0xFF, 0xE7, 0xFF, 0xF0, 0x0F,
  0xFF, 0xE7, 0xFF, 0xF0, 0x0F, 0xFF, 0xE7, 0xFF, 0xF0, 0x0F, 0xFF, 0xE7,
  0xFF, 0xF0, 0x0F, 0xFF, 0xE7, 0xFF, 0xF0, 0x0F, 0xFF, 0xE7, 0xFF, 0xF0,
  0x0F, 0xFF, 0xE7, 0xFF, 0xF0, 0x0F, 0xFF, 0xE7, 0xFF, 0xF0, 0x0F, 0xFF,
  0xE7, 0xFF, 0xF0, 0x0F, 0xFF, 0xE7, 0xFF, 0xF0, 0x0F, 0xFF, 0xE7, 0xFF,
  0xF0, 0x0F, 0xFF, 0xE7, 0xFF, 0xF0, 0x0F, 0xFF, 0xE7, 0xFF, 0xF0, 0x0F,
  0xFF, 0xE7, 0xFF, 0xF0, 0x07, 0xFF, 0xE7, 0xFF, 0xE0, 0x07, 0xF8, 0x00,
  0x1F, 0xE0, 0x02, 0x00, 0x00, 0x00, 0x40,
]))
const TRAILER_0_BRIGHTBLUE = layer(0, 2, 40, 45, new Uint8Array([
  0x00, 0x07, 0xFF, 0xE0, 0x00, 0x00, 0x0F, 0xFF, 0xF0, 0x00, 0x00, 0x1F,
  0xFF, 0xF8, 0x00, 0x00, 0x3F, 0xFF, 0xFC, 0x00, 0x00, 0x3F, 0xFF, 0xFC,
  0x00, 0x00, 0x7F, 0xFF, 0xFE, 0x00, 0x00, 0xFF, 0xFF, 0xFF, 0x00, 0x01,
  0xFF, 0xFF, 0xFF, 0x80, 0x03, 0xFF, 0xFF, 0xFF, 0xC0, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x04, 0x00, 0x00, 0x00, 0x20,
  0x0C, 0x00, 0x00, 0x00, 0x30, 0x1C, 0x00, 0x00, 0x00, 0x38, 0x1C, 0x00,
  0x00, 0x00, 0x38, 0x1C, 0x00, 0x00, 0x00, 0x38, 0x1C, 0x00, 0x00, 0x00,
  0x38, 0x1C, 0x00, 0x00, 0x00, 0x38, 0x1C, 0x00, 0x00, 0x00, 0x38, 0x1C,
  0x00, 0x00, 0x00, 0x38, 0x1C, 0x00, 0x00, 0x00, 0x38, 0x1C, 0x00, 0x00,
  0x00, 0x38, 0x1C, 0x00, 0x00, 0x00, 0x38, 0x1C, 0x00, 0x00, 0x00, 0x38,
  0x1C, 0x00, 0x00, 0x00, 0x38, 0x1C, 0x00, 0x00, 0x00, 0x38, 0x1C, 0x00,
  0x00, 0x00, 0x38, 0x1C, 0x00, 0x00, 0x00, 0x38, 0x1C, 0x00, 0x00, 0x00,
  0x38, 0x1C, 0x00, 0x00, 0x00, 0x38, 0x1C, 0x00, 0x00, 0x00, 0x38, 0x1C,
  0x00, 0x00, 0x00, 0x38, 0x1C, 0x00, 0x00, 0x00, 0x38, 0x1C, 0x00, 0x00,
  0x00, 0x38, 0x1C, 0x00, 0x00, 0x00, 0x38, 0x1C, 0x00, 0x00, 0x00, 0x38,
  0x1C, 0x00, 0x00, 0x00, 0x38, 0x1C, 0x00, 0x00, 0x00, 0x38, 0x1C, 0x00,
  0x00, 0x00, 0x38, 0x1C, 0x00, 0x00, 0x00, 0x38, 0x1C, 0x00, 0x00, 0x00,
  0x38, 0x1C, 0x00, 0x00, 0x00, 0x38, 0x1C, 0x00, 0x00, 0x00, 0x38, 0x0C,
  0x00, 0x00, 0x00, 0x30, 0x04, 0x00, 0x00, 0x00, 0x20,
]))
const TRAILER_0_DETAIL = layer(0, 12, 40, 39, new Uint8Array([
  0x0F, 0xFF, 0xFF, 0xFF, 0xF0, 0x0F, 0xFF, 0xFF, 0xFF, 0xF0, 0x00, 0x00,
  0x18, 0x00, 0x00, 0x00, 0x00, 0x18, 0x00, 0x00, 0x00, 0x00, 0x18, 0x00,
  0x00, 0x00, 0x00, 0x18, 0x00, 0x00, 0x03, 0x80, 0x18, 0x01, 0xC0, 0x03,
  0x80, 0x18, 0x01, 0xC0, 0x00, 0x00, 0x18, 0x00, 0x00, 0x00, 0x00, 0x18,
  0x00, 0x00, 0x00, 0x00, 0x18, 0x00, 0x00, 0x00, 0x00, 0x18, 0x00, 0x00,
  0x0F, 0xFF, 0xFF, 0xFF, 0xF0, 0x00, 0x00, 0x18, 0x00, 0x00, 0x00, 0x00,
  0x18, 0x00, 0x00, 0x00, 0x00, 0x18, 0x00, 0x00, 0x00, 0x00, 0x18, 0x00,
  0x00, 0x00, 0x00, 0x18, 0x00, 0x00, 0x00, 0x00, 0x18, 0x00, 0x00, 0x03,
  0x80, 0x18, 0x01, 0xC0, 0x03, 0x80, 0x18, 0x01, 0xC0, 0x00, 0x00, 0x18,
  0x00, 0x00, 0x00, 0x00, 0x18, 0x00, 0x00, 0x0F, 0xFF, 0xFF, 0xFF, 0xF0,
  0x00, 0x00, 0x7E, 0x00, 0x00, 0x00, 0x00, 0x7E, 0x00, 0x00, 0x00, 0x00,
  0x7E, 0x00, 0x00, 0x00, 0x00, 0x7E, 0x00, 0x00, 0x00, 0x00, 0x7E, 0x00,
  0x00, 0x03, 0x80, 0x7E, 0x01, 0xC0, 0x03, 0x80, 0x18, 0x01, 0xC0, 0x00,
  0x00, 0x18, 0x00, 0x00, 0x00, 0x00, 0x18, 0x00, 0x00, 0x00, 0x00, 0x18,
  0x00, 0x00, 0x0F, 0xFF, 0xFF, 0xFF, 0xF0, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x07, 0xFF, 0xFF, 0xFF, 0xE0, 0x07, 0xFF,
  0xFF, 0xFF, 0xE0,
]))
const TRAILER_0_CYAN = layer(0, 0, 40, 58, new Uint8Array([
  0x00, 0x07, 0xFF, 0xE0, 0x00, 0x00, 0x08, 0x00, 0x10, 0x00, 0x00, 0x10,
  0x00, 0x08, 0x00, 0x00, 0x20, 0x00, 0x04, 0x00, 0x00, 0x20, 0x00, 0x04,
  0x00, 0x00, 0x40, 0x00, 0x02, 0x00, 0x00, 0x80, 0x00, 0x01, 0x00, 0x01,
  0x00, 0x00, 0x00, 0x80, 0x01, 0x00, 0x00, 0x00, 0x80, 0x02, 0x00, 0x00,
  0x00, 0x40, 0x04, 0x00, 0x00, 0x00, 0x20, 0x07, 0xFF, 0xFF, 0xFF, 0xE0,
  0x08, 0x00, 0x00, 0x00, 0x10, 0x08, 0x00, 0x00, 0x00, 0x10, 0x08, 0x00,
  0x00, 0x00, 0x10, 0x10, 0x00, 0x00, 0x00, 0x08, 0x10, 0x00, 0x00, 0x00,
  0x08, 0x10, 0x00, 0x00, 0x00, 0x08, 0x10, 0x00, 0x00, 0x00, 0x08, 0x10,
  0x00, 0x00, 0x00, 0x08, 0x10, 0x00, 0x00, 0x00, 0x08, 0x10, 0x00, 0x00,
  0x00, 0x08, 0x10, 0x00, 0x00, 0x00, 0x08, 0x10, 0x00, 0x00, 0x00, 0x08,
  0x10, 0x00, 0x00, 0x00, 0x08, 0x10, 0x00, 0x00, 0x00, 0x08, 0x10, 0x00,
  0x00, 0x00, 0x08, 0x10, 0x00, 0x00, 0x00, 0x08, 0x10, 0x00, 0x00, 0x00,
  0x08, 0x10, 0x00, 0x00, 0x00, 0x08, 0x10, 0x00, 0x00, 0x00, 0x08, 0x10,
  0x00, 0x00, 0x00, 0x08, 0x10, 0x00, 0x00, 0x00, 0x08, 0x10, 0x00, 0x00,
  0x00, 0x08, 0x10, 0x00, 0x00, 0x00, 0x08, 0x10, 0x00, 0x00, 0x00, 0x08,
  0x10, 0x00, 0x00, 0x00, 0x08, 0x10, 0x00, 0x00, 0x00, 0x08, 0x10, 0x00,
  0x00, 0x00, 0x08, 0x10, 0x00, 0x00, 0x00, 0x08, 0x10, 0x00, 0x00, 0x00,
  0x08, 0x10, 0x00, 0x00, 0x00, 0x08, 0x10, 0x00, 0x00, 0x00, 0x08, 0x10,
  0x00, 0x00, 0x00, 0x08, 0x10, 0x00, 0x00, 0x00, 0x08, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x78, 0x00, 0x1E, 0x00, 0x00, 0x78, 0x00, 0x1E, 0x00, 0x00, 0x78, 0x00,
  0x1E, 0x00,
]))
const TRAILER_0_WHITE = layer(0, 1, 40, 29, new Uint8Array([
  0x00, 0x03, 0xFF, 0xC0, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0xFF, 0xC0, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x03, 0x00, 0x00, 0x00, 0x00, 0x03, 0x00,
  0x00, 0x00, 0x00, 0x03, 0x00, 0x00, 0x00, 0x00, 0x03, 0x00, 0x00, 0x00,
  0x00, 0x03, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00,
  0x00, 0x00, 0x00, 0x01, 0x80, 0x00, 0x00, 0x00, 0x01, 0x80, 0x00, 0x00,
  0x00, 0x01, 0x80, 0x00, 0x00, 0x00, 0x01, 0x80, 0x00, 0x00, 0x00, 0x01,
  0x80,
]))
const TRAILER_0_YELLOW = layer(16, 47, 8, 3, new Uint8Array([
  0x7E, 0x7E, 0x7E,
]))
const TRAILER_0_LAMPS = layer(0, 43, 40, 4, new Uint8Array([
  0x07, 0x80, 0x00, 0x01, 0xE0, 0x07, 0x80, 0x00, 0x01, 0xE0, 0x07, 0x80,
  0x00, 0x01, 0xE0, 0x07, 0x80, 0x00, 0x01, 0xE0,
]))

export interface PlayerTruckCabPose {
  readonly base: PlayerTruckLayer
  readonly red: PlayerTruckLayer
  readonly detail: PlayerTruckLayer
  readonly cyan: PlayerTruckLayer
}

export interface PlayerTruckTrailerPose {
  readonly base: PlayerTruckLayer
  readonly blue: PlayerTruckLayer
  readonly brightBlue: PlayerTruckLayer
  readonly detail: PlayerTruckLayer
  readonly cyan: PlayerTruckLayer
  readonly white: PlayerTruckLayer
  readonly yellow: PlayerTruckLayer
  readonly lamps: PlayerTruckLayer
}

function mirrorLayer(source: PlayerTruckLayer, componentWidth: number): PlayerTruckLayer {
  return {
    x: componentWidth - source.x - source.bitmap.width,
    y: source.y,
    bitmap: mirrorBitmap(source.bitmap),
  }
}

function mirrorCabPose(source: PlayerTruckCabPose): PlayerTruckCabPose {
  return {
    base: mirrorLayer(source.base, CAB_W),
    red: mirrorLayer(source.red, CAB_W),
    detail: mirrorLayer(source.detail, CAB_W),
    cyan: mirrorLayer(source.cyan, CAB_W),
  }
}

function mirrorTrailerPose(source: PlayerTruckTrailerPose): PlayerTruckTrailerPose {
  return {
    base: mirrorLayer(source.base, TRAILER_W),
    blue: mirrorLayer(source.blue, TRAILER_W),
    brightBlue: mirrorLayer(source.brightBlue, TRAILER_W),
    detail: mirrorLayer(source.detail, TRAILER_W),
    cyan: mirrorLayer(source.cyan, TRAILER_W),
    white: mirrorLayer(source.white, TRAILER_W),
    yellow: mirrorLayer(source.yellow, TRAILER_W),
    lamps: mirrorLayer(source.lamps, TRAILER_W),
  }
}

const CAB_HARD_LEFT: PlayerTruckCabPose = {
  base: CAB_M2_BASE, red: CAB_M2_RED, detail: CAB_M2_DETAIL, cyan: CAB_M2_CYAN,
}
const CAB_LEFT: PlayerTruckCabPose = {
  base: CAB_M1_BASE, red: CAB_M1_RED, detail: CAB_M1_DETAIL, cyan: CAB_M1_CYAN,
}
const CAB_STRAIGHT: PlayerTruckCabPose = {
  base: CAB_0_BASE, red: CAB_0_RED, detail: CAB_0_DETAIL, cyan: CAB_0_CYAN,
}

const TRAILER_HARD_LEFT: PlayerTruckTrailerPose = {
  base: TRAILER_M2_BASE,
  blue: TRAILER_M2_BLUE,
  brightBlue: TRAILER_M2_BRIGHTBLUE,
  detail: TRAILER_M2_DETAIL,
  cyan: TRAILER_M2_CYAN,
  white: TRAILER_M2_WHITE,
  yellow: TRAILER_M2_YELLOW,
  lamps: TRAILER_M2_LAMPS,
}
const TRAILER_LEFT: PlayerTruckTrailerPose = {
  base: TRAILER_M1_BASE,
  blue: TRAILER_M1_BLUE,
  brightBlue: TRAILER_M1_BRIGHTBLUE,
  detail: TRAILER_M1_DETAIL,
  cyan: TRAILER_M1_CYAN,
  white: TRAILER_M1_WHITE,
  yellow: TRAILER_M1_YELLOW,
  lamps: TRAILER_M1_LAMPS,
}
const TRAILER_STRAIGHT: PlayerTruckTrailerPose = {
  base: TRAILER_0_BASE,
  blue: TRAILER_0_BLUE,
  brightBlue: TRAILER_0_BRIGHTBLUE,
  detail: TRAILER_0_DETAIL,
  cyan: TRAILER_0_CYAN,
  white: TRAILER_0_WHITE,
  yellow: TRAILER_0_YELLOW,
  lamps: TRAILER_0_LAMPS,
}

const CAB_POSES: Readonly<Record<TruckAngle, PlayerTruckCabPose>> = {
  [-2]: CAB_HARD_LEFT,
  [-1]: CAB_LEFT,
  [0]: CAB_STRAIGHT,
  [1]: mirrorCabPose(CAB_LEFT),
  [2]: mirrorCabPose(CAB_HARD_LEFT),
}

const TRAILER_POSES: Readonly<Record<TruckAngle, PlayerTruckTrailerPose>> = {
  [-2]: TRAILER_HARD_LEFT,
  [-1]: TRAILER_LEFT,
  [0]: TRAILER_STRAIGHT,
  [1]: mirrorTrailerPose(TRAILER_LEFT),
  [2]: mirrorTrailerPose(TRAILER_HARD_LEFT),
}

/** Small packed runtime bitmaps; no screen-sized image data is retained. */
export const PLAYER_TRUCK_POSES = { cab: CAB_POSES, trailer: TRAILER_POSES } as const

function drawLayer(
  ctx: CanvasRenderingContext2D,
  source: PlayerTruckLayer,
  x: number,
  y: number,
  color: SpectrumColor,
): void {
  drawBitmap(ctx, source.bitmap, x + source.x, y + source.y, color)
}

export interface PlayerTruckArticulation {
  cabYaw: number
  trailerYaw: number
  cabAngle: TruckAngle
  trailerAngle: TruckAngle
}

function clampSteering(value: number): number {
  return Math.max(-1, Math.min(1, value))
}

/**
 * How far past a pose boundary the yaw must travel before the pose changes.
 *
 * Without it a yaw resting on a threshold flips between two poses every frame,
 * and the yaw now comes from lateral velocity, which drifts across a boundary
 * far more slowly than a key press did. At 90 km/h the yaw moves about 0.012 per
 * frame, so this is roughly four frames of clearance — enough to stop a flicker,
 * small enough not to feel sticky. `vehicleLod.ts` chose hysteresis over the same
 * problem for the same reason.
 */
const ANGLE_HYSTERESIS = 0.05

function quantizeRaw(clamped: number): TruckAngle {
  const scaled = clamped * 2
  const rounded = scaled < 0 ? -Math.round(-scaled) : Math.round(scaled)
  if (Object.is(rounded, -0)) return 0
  return Math.max(-2, Math.min(2, rounded)) as TruckAngle
}

/**
 * Yaw to one of the five drawn poses.
 *
 * Pass `previous` to get hysteresis; omit it for the raw nearest pose.
 */
export function quantizeTruckAngle(yaw: number, previous?: TruckAngle): TruckAngle {
  const clamped = clampSteering(yaw)
  const next = quantizeRaw(clamped)
  if (previous === undefined || next === previous) return next

  // Boundary between the pose held now and the neighbour we would move to. Poses
  // sit every 0.5 in scaled space, so their midpoints are every 0.25 in yaw.
  const step = next > previous ? 1 : -1
  const boundary = (previous + step * 0.5) / 2
  const cleared = step > 0
    ? clamped >= boundary + ANGLE_HYSTERESIS
    : clamped <= boundary - ANGLE_HYSTERESIS
  return cleared ? next : previous
}

export function createPlayerTruckArticulation(steering = 0): PlayerTruckArticulation {
  const yaw = clampSteering(steering)
  const angle = quantizeTruckAngle(yaw)
  return { cabYaw: yaw, trailerYaw: yaw, cabAngle: angle, trailerAngle: angle }
}

/** Mutates and returns the same object; dt is clamped to 50 ms. */
/**
 * Advances the articulation towards what the truck is actually doing.
 *
 * `steering` is **not** the key: it is lateral velocity as a fraction of the most
 * the truck can carry, so the cab reports the manoeuvre rather than the button.
 * Driving it from the key meant the first pose appeared 29 ms after the press —
 * under two frames — and a held key saturated the yaw, so only *straight* and
 * *full lock* were ever really seen. The three poses in between existed but were
 * passed through, never rested in.
 *
 * Lateral velocity already carries the truck's own inertia, so the cab needs only
 * a short smoothing of its own; the 280 ms on the trailer is the articulation
 * itself, a trailer swinging behind a tractor rather than with it.
 *
 * This changes nothing about how fast the player's input reaches the road. The
 * physics is untouched: only what the drawing is derived *from* has moved.
 */
export function updatePlayerTruckArticulation(
  state: PlayerTruckArticulation,
  steering: number,
  dtMs: number,
): PlayerTruckArticulation {
  const target = clampSteering(steering)
  const dt = Math.max(0, Math.min(50, dtMs))
  state.cabYaw += (target - state.cabYaw) * (1 - Math.exp(-dt / 60))
  state.trailerYaw += (state.cabYaw - state.trailerYaw) * (1 - Math.exp(-dt / 280))
  state.cabAngle = quantizeTruckAngle(state.cabYaw, state.cabAngle)
  state.trailerAngle = quantizeTruckAngle(state.trailerYaw, state.trailerAngle)
  return state
}

export function playerTruckOrigin(cx: number, baseY: number, lean = 0): { x: number; y: number } {
  return {
    x: Math.round(cx - PLAYER_TRUCK_W / 2 + lean),
    y: Math.round(baseY - PLAYER_TRUCK_H),
  }
}

export const PLAYER_TRUCK_LAMP_COLORS = { rolling: C.RED, braking: C.B_RED } as const

/** Cabin first, then trailer: straight ahead hides the tractor; articulation reveals its flank. */
export function drawPlayerTruck(
  ctx: CanvasRenderingContext2D,
  cx: number,
  baseY: number,
  state: Readonly<PlayerTruckArticulation>,
  braking = false,
  lean = 0,
): void {
  const origin = playerTruckOrigin(cx, baseY, lean)
  const cabX = origin.x + CAB_X_BY_ANGLE[state.cabAngle]
  const cabY = origin.y + CAB_Y
  const cab = CAB_POSES[state.cabAngle]
  drawLayer(ctx, cab.base, cabX, cabY, C.BLACK)
  drawLayer(ctx, cab.red, cabX, cabY, C.RED)
  drawLayer(ctx, cab.cyan, cabX, cabY, C.B_CYAN)
  drawLayer(ctx, cab.detail, cabX, cabY, C.BLACK)

  const trailerX = origin.x + TRAILER_X
  const trailerY = origin.y + TRAILER_Y
  const trailer = TRAILER_POSES[state.trailerAngle]
  drawLayer(ctx, trailer.base, trailerX, trailerY, C.BLACK)
  drawLayer(ctx, trailer.blue, trailerX, trailerY, C.BLUE)
  drawLayer(ctx, trailer.brightBlue, trailerX, trailerY, C.B_BLUE)
  drawLayer(ctx, trailer.detail, trailerX, trailerY, C.BLACK)
  drawLayer(ctx, trailer.cyan, trailerX, trailerY, C.B_CYAN)
  drawLayer(ctx, trailer.white, trailerX, trailerY, C.B_WHITE)
  drawLayer(ctx, trailer.lamps, trailerX, trailerY, braking ? C.B_RED : C.RED)
  drawLayer(ctx, trailer.yellow, trailerX, trailerY, C.B_YELLOW)
}

function blitBitmap(target: Uint8Array, source: Bitmap, offsetX: number, offsetY: number): void {
  const targetBytesPerRow = PLAYER_TRUCK_W / 8
  const sourceBytesPerRow = source.width / 8
  for (let y = 0; y < source.height; y++) {
    for (let x = 0; x < source.width; x++) {
      const sourceByte = source.data[y * sourceBytesPerRow + (x >> 3)]!
      if ((sourceByte & (0x80 >> (x & 7))) === 0) continue
      const tx = offsetX + x
      const ty = offsetY + y
      if (tx < 0 || tx >= PLAYER_TRUCK_W || ty < 0 || ty >= PLAYER_TRUCK_H) {
        throw new Error(`player truck collision pixel outside ${PLAYER_TRUCK_W}x${PLAYER_TRUCK_H}`)
      }
      target[ty * targetBytesPerRow + (tx >> 3)]! |= 0x80 >> (tx & 7)
    }
  }
}

function mergeCollision(cabAngle: TruckAngle, trailerAngle: TruckAngle): Bitmap {
  const data = new Uint8Array((PLAYER_TRUCK_W / 8) * PLAYER_TRUCK_H)
  const cab = CAB_POSES[cabAngle].base
  const trailer = TRAILER_POSES[trailerAngle].base
  blitBitmap(data, cab.bitmap, CAB_X_BY_ANGLE[cabAngle] + cab.x, CAB_Y + cab.y)
  blitBitmap(data, trailer.bitmap, TRAILER_X + trailer.x, TRAILER_Y + trailer.y)
  return createBitmap(data, PLAYER_TRUCK_W, PLAYER_TRUCK_H)
}

/** All 25 unions are built once; render/update/collision allocate nothing per frame. */
export const PLAYER_TRUCK_COLLISION_BITMAPS: readonly (readonly Bitmap[])[] =
  PLAYER_TRUCK_ANGLES.map((cabAngle) =>
    PLAYER_TRUCK_ANGLES.map((trailerAngle) => mergeCollision(cabAngle, trailerAngle)),
  )

export const PLAYER_TRUCK_COLLISION_MASKS: readonly (readonly PixelMask[])[] =
  PLAYER_TRUCK_COLLISION_BITMAPS.map((row) => row.map(bitmapPixelMask))

// The sprite is a rear-high projection: its upper body is not a tyre touching
// the shoulder. Off-road physics therefore follows the two tyre contact patches,
// while traffic collision keeps the complete articulated body silhouette.
const ROAD_CONTACT_TOP = PLAYER_TRUCK_H - 12
const ROAD_LEFT_TYRE = { left: 6, right: 15 } as const
const ROAD_RIGHT_TYRE = { left: 24, right: 33 } as const
const EMPTY_MASK_ROW: readonly number[] = []

function roadContactMask(source: PixelMask): PixelMask {
  const rows = source.rows.map((row, y) => {
    if (y < ROAD_CONTACT_TOP) return EMPTY_MASK_ROW
    return row.filter((x) =>
      (x >= ROAD_LEFT_TYRE.left && x <= ROAD_LEFT_TYRE.right)
      || (x >= ROAD_RIGHT_TYRE.left && x <= ROAD_RIGHT_TYRE.right),
    )
  })
  return {
    width: source.width,
    height: source.height,
    rows,
    totalPixels: rows.reduce((sum, row) => sum + row.length, 0),
  }
}

export const PLAYER_TRUCK_ROAD_MASKS: readonly (readonly PixelMask[])[] =
  PLAYER_TRUCK_COLLISION_MASKS.map((row) => row.map(roadContactMask))

export function getPlayerTruckCollisionBitmap(state: Readonly<PlayerTruckArticulation>): Bitmap {
  return PLAYER_TRUCK_COLLISION_BITMAPS[state.cabAngle + 2]![state.trailerAngle + 2]!
}

export function getPlayerTruckCollisionMask(state: Readonly<PlayerTruckArticulation>): PixelMask {
  return PLAYER_TRUCK_COLLISION_MASKS[state.cabAngle + 2]![state.trailerAngle + 2]!
}

export function getPlayerTruckRoadMask(state: Readonly<PlayerTruckArticulation>): PixelMask {
  return PLAYER_TRUCK_ROAD_MASKS[state.cabAngle + 2]![state.trailerAngle + 2]!
}

export const PLAYER_TRUCK_STRAIGHT_COLLISION = PLAYER_TRUCK_COLLISION_BITMAPS[2]![2]!

export interface PlayerTruckPoint {
  readonly dx: number
  readonly dy: number
}

function measureLampPositions(angle: TruckAngle): readonly PlayerTruckPoint[] {
  const source = TRAILER_POSES[angle].lamps
  const sums = [{ x: 0, y: 0, count: 0 }, { x: 0, y: 0, count: 0 }]
  const bytesPerRow = source.bitmap.width / 8
  for (let y = 0; y < source.bitmap.height; y++) {
    for (let x = 0; x < source.bitmap.width; x++) {
      const byte = source.bitmap.data[y * bytesPerRow + (x >> 3)]!
      if ((byte & (0x80 >> (x & 7))) === 0) continue
      const dx = TRAILER_X + source.x + x + 0.5
      const dy = TRAILER_Y + source.y + y + 0.5
      const sum = sums[dx < PLAYER_TRUCK_W / 2 ? 0 : 1]!
      sum.x += dx
      sum.y += dy
      sum.count++
    }
  }
  return sums.map((sum) => ({ dx: sum.x / sum.count, dy: sum.y / sum.count }))
}

const LAMP_POSITIONS = Object.fromEntries(
  PLAYER_TRUCK_ANGLES.map((angle) => [angle, measureLampPositions(angle)]),
) as Record<TruckAngle, readonly PlayerTruckPoint[]>

const WHEEL_POSITIONS: readonly PlayerTruckPoint[] = [
  { dx: 11, dy: TRAILER_Y + 56.5 },
  { dx: 29, dy: TRAILER_Y + 56.5 },
]

export function getPlayerTruckLampPositions(
  state: Readonly<Pick<PlayerTruckArticulation, 'trailerAngle'>>,
): readonly PlayerTruckPoint[] {
  return LAMP_POSITIONS[state.trailerAngle]
}

export function getPlayerTruckWheelPositions(
  _state: Readonly<Pick<PlayerTruckArticulation, 'trailerAngle'>>,
): readonly PlayerTruckPoint[] {
  return WHEEL_POSITIONS
}

export function pushPlayerTruckLampSpots(
  out: GlowSource[],
  cx: number,
  baseY: number,
  state: Readonly<PlayerTruckArticulation>,
  braking: boolean,
  lean = 0,
): void {
  const origin = playerTruckOrigin(cx, baseY, lean)
  const scale = glowRadiusScale()
  const intensity = braking ? TRUCK_GLOW_BRAKE_INTENSITY : TRUCK_GLOW_INTENSITY
  const radius = (braking ? TRUCK_GLOW_BRAKE_RADIUS : TRUCK_GLOW_RADIUS) * scale
  for (const lamp of getPlayerTruckLampPositions(state)) {
    const x = origin.x + lamp.dx
    const y = origin.y + lamp.dy
    out.push({ x, y, radius, color: C.B_RED, intensity })
    if (braking) {
      out.push({
        x, y,
        radius: TRUCK_GLOW_CORE_RADIUS * scale,
        color: C.B_WHITE,
        intensity: GLOW_CORE_INTENSITY,
      })
    }
  }
}
